import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { posApiFetch } from "@/utils/pos-api";
import { Clock, MapPin, Maximize, Minimize, Hourglass, CircleCheckBig } from "lucide-react";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import { MultiSelect } from "@/components/pos/admin/common/multi-select";
import { usePosWebSocket } from "@/hooks/pos/use-pos-ws-adapter";

type KitchenState = {
  location?: string[];
};

const defaultKitchenState: KitchenState = {
  location: ["all"],
};

export default function LiveOrdersPage() {
  return <LiveOrdersContent />;
}

function LiveOrdersContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const [kitchenState, setKitchenState] = usePagePersistentState<KitchenState>("kitchen", defaultKitchenState);
  const selectedLocationIds = kitchenState.location ?? ["all"];

  useEffect(() => {
    document.title = "Live Orders | Admin";
  }, []);

  const { lastMessage } = usePosWebSocket({
    channels: ["orders"],
    enabled: true,
  });

  useEffect(() => {
    if (lastMessage?.type === "order_update" || lastMessage?.type === "new_order") {
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/orders"] });
    }
  }, [lastMessage, queryClient]);

  const fetchOrders = async () => {
    const res = await posApiFetch("/v1/pos/orders");
    if (!res.ok) throw new Error("Failed to fetch orders");
    return res.json();
  };

  const fetchLocations = async () => {
    const res = await posApiFetch("/v1/pos/locations");
    if (!res.ok) throw new Error("Failed to fetch locations");
    return res.json();
  };

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["/v1/pos/orders"],
    queryFn: fetchOrders,
  });

  const { data: locations = [], isLoading: isLocationLoading } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: fetchLocations,
    retry: 3,
    retryDelay: 1000,
  });

  const orderLocationIds = useMemo(() => {
    return new Set(orders.map((order: any) => order?.location?.id?.toString()).filter(Boolean));
  }, [orders]);

  const toggleFullScreen = () => setIsFullScreen((prev) => !prev);

  const renderOrderItemName = (item: any) => {
    const name = item.product?.name ?? item.customDrink?.name ?? "Item";
    const hasCustomDrink = item.customDrink && item.customDrink.components?.length > 0;
    const hasModifiers = item.modifiers && item.modifiers.length > 0;

    if (!hasCustomDrink && !hasModifiers) return name;

    const components = hasCustomDrink
      ? item.customDrink.components.map((c: any) => c.name).join(", ")
      : null;
    const modifierText = hasModifiers
      ? item.modifiers.map((m: any) => m.modifierName).join(", ")
      : null;

    return (
      <>
        <span>{name}</span>
        {components && (
          <div className="ml-4 mt-1 text-[12px] leading-4 text-[var(--secondary-text-color)]">
            • {components}
          </div>
        )}
        {modifierText && (
          <div className="ml-4 mt-1 text-[12px] leading-4 text-[var(--secondary-text-color)]">
            • {modifierText}
          </div>
        )}
      </>
    );
  };

  const updateOrderStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const response = await posApiFetch(`/v1/pos/orders/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error("Failed to update order status");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/orders"] });
      toast({ title: "Order status updated" });
    },
    onError: () => {
      toast({ title: "Failed to update order status", variant: "destructive" });
    },
  });

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case "pending": return "preparing";
      case "preparing": return "ready";
      case "ready": return "delivered";
      default: return currentStatus;
    }
  };

  const getNextStatusLabel = (currentStatus: string) => {
    switch (currentStatus) {
      case "pending": return "Start Preparing";
      case "preparing": return "Mark Ready";
      case "ready": return "Mark Delivered";
      default: return "Update Status";
    }
  };

  const activeOrders = orders
    .filter((order: any) => order.status !== "delivered" && order.status !== "cancelled")
    .filter((order: any) => {
      const orderLocId = order.location?.id?.toString();
      if (selectedLocationIds.includes("all")) return true;
      return !!orderLocId && selectedLocationIds.includes(orderLocId);
    });

  const pendingOrders = activeOrders.filter((order: any) => order.status === "pending");
  const preparingOrders = activeOrders.filter((order: any) => order.status === "preparing");
  const readyOrders = activeOrders.filter((order: any) => order.status === "ready");

  const latestPendingOrderId: number | null = (() => {
    if (!pendingOrders || pendingOrders.length === 0) return null;
    const withDates = pendingOrders.filter((o: any) => !!o.createdAt && !Number.isNaN(new Date(o.createdAt).getTime()));
    if (withDates.length > 0) {
      const latest = withDates.reduce((best: any, o: any) => {
        return new Date(o.createdAt!).getTime() > new Date(best.createdAt!).getTime() ? o : best;
      }, withDates[0]);
      return latest.id;
    }
    return Math.max(...pendingOrders.map((o: any) => o.id));
  })();

  const renderOrderCard = (order: any, buttonColorClass: string) => (
    <div
      key={order.id}
      className={`bg-[var(--primary-background-color)] p-3 rounded-[8px] border-0 ${
        order.id === latestPendingOrderId && order.status === "pending" ? "animate-pulse" : ""
      }`}
    >
      <div className="flex justify-between items-center mb-3">
        <div className="flex flex-col gap-2">
          <h3 className="text-[14px] leading-[16px] font-bold text-[var(--primary-text-color)]">{order.orderNumber}</h3>
          <span className="text-[12px] leading-[16px] text-[var(--primary-text-color)]">{order?.user?.pickupPerson}</span>
        </div>
        <div className="flex items-center text-[13px] leading-[20px] font-normal text-[var(--secondary-text-color)]">
          <MapPin className="h-4 w-4 mr-1" />
          {order?.location?.name} {order.locationDetails && `• ${order.locationDetails}`}
        </div>
      </div>

      <div className="flex justify-between items-center mb-3 pt-3 border-t border-dashed border-[#C3C6D4]">
        <div className="w-full flex flex-col gap-[6px]">
          {order.items.map((item: any) => (
            <div key={item.id} className="text-[13px] flex flex-col gap-1 leading-5 font-normal text-[var(--secondary-text-color)]">
              <div>{renderOrderItemName(item)} x {item.quantity}</div>
            </div>
          ))}
        </div>
      </div>

      {order.notes && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-[12px] text-yellow-800">
          <span className="font-medium">Note:</span> {order.notes}
        </div>
      )}

      <div className="flex justify-between items-center">
        <Button
          size="sm"
          className={`bg-[var(--primary-background-color)] w-full h-[32px] rounded-[4px] text-[13px] leading-[20px] font-normal ${buttonColorClass}`}
          onClick={() =>
            updateOrderStatusMutation.mutate({
              id: order.id,
              status: getNextStatus(order.status),
            })
          }
          disabled={updateOrderStatusMutation.isPending}
        >
          {getNextStatusLabel(order.status)}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={isFullScreen ? "fixed inset-0 z-50 bg-[var(--primary-background-color)]" : ""}>
      <div className="dashboard-header h-[48px] px-[16px] flex items-center justify-between w-full border-b border-[var(--layout-border-color)] sticky top-0 bg-[var(--primary-background-color)] z-50">
        <span className="text-[20px] leading-[30px] font-bold">Live Orders</span>

        <div className="flex items-center gap-2">
          <Button onClick={toggleFullScreen} className="h-[32px] px-2 rounded-[4px]" variant="ghost">
            {isFullScreen ? (
              <>
                <Minimize className="h-5 w-5" />
                <span className="text-[13px] leading-[15px] font-medium text-[#172b4d]">Exit Full Screen</span>
              </>
            ) : (
              <>
                <Maximize className="h-5 w-5" />
                <span className="text-[13px] leading-[15px] font-medium text-[#172b4d]">Full Screen</span>
              </>
            )}
          </Button>

          <div className="w-[150px]">
            <MultiSelect
              options={[
                { label: "All", value: "all", colorCode: "#6B7280" },
                ...locations
                  .filter((loc: any) => loc.active || orderLocationIds.has(String(loc.id)))
                  .map((loc: any) => ({
                    label: loc.name,
                    value: loc.id.toString(),
                    colorCode: loc.colorCode,
                  })),
              ]}
              selected={selectedLocationIds}
              onChange={(values: (string | number)[]) => {
                const newSelected = values.map((v) => String(v));
                if (!newSelected || newSelected.length === 0) {
                  setKitchenState((prev) => ({ ...prev, location: ["all"] }));
                  return;
                }
                if (newSelected.includes("all") && !selectedLocationIds.includes("all")) {
                  setKitchenState((prev) => ({ ...prev, location: ["all"] }));
                  return;
                }
                if (selectedLocationIds.includes("all") && newSelected.length > 1) {
                  const withoutAll = newSelected.filter((v) => v !== "all");
                  setKitchenState((prev) => ({ ...prev, location: withoutAll }));
                  return;
                }
                setKitchenState((prev) => ({ ...prev, location: newSelected }));
              }}
              placeholder="Select locations"
              isChipsRequired={false}
              loading={isLocationLoading}
            />
          </div>
        </div>
      </div>

      <div className="p-4 h-[calc(100%-48px)]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-full">
          {/* Pending column */}
          <Card className="p-3 bg-[#FDFDF1] border-0 flex flex-col min-h-full overflow-hidden">
            <CardHeader className="p-0 shrink-0">
              <CardTitle className="flex items-center text-[14px] leading-[18px] mb-5 font-bold text-[var(--primary-text-color)]">
                <Clock className="h-5 w-5 text-[#676879] mr-1" />
                Pending ({pendingOrders.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 border-0 flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="animate-pulse bg-gray-200 h-32 rounded-[8px]" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {pendingOrders.length === 0 ? (
                    <p className="text-gray-400 text-[13px] text-center py-8">No pending orders</p>
                  ) : (
                    pendingOrders.map((order: any) =>
                      renderOrderCard(order, "border border-[--primary-color] text-[--primary-color] hover:bg-[--primary-color] hover:text-[#ffffff]")
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preparing column */}
          <Card className="p-3 bg-[#EFF6FF] border-0 flex flex-col min-h-full overflow-hidden">
            <CardHeader className="p-0 shrink-0">
              <CardTitle className="flex items-center text-[14px] leading-[18px] font-bold text-[var(--primary-text-color)] mb-5">
                <Hourglass className="h-5 w-5 text-[#676879] mr-2" />
                Preparing ({preparingOrders.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="animate-pulse bg-gray-200 h-32 rounded-[8px]" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {preparingOrders.length === 0 ? (
                    <p className="text-gray-400 text-[13px] text-center py-8">No orders being prepared</p>
                  ) : (
                    preparingOrders.map((order: any) =>
                      renderOrderCard(order, "border border-[#1F76C2] text-[#1F76C2] hover:bg-[#1F76C2] hover:text-[#ffffff]")
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ready column */}
          <Card className="p-3 border-0 bg-[#E9F8EE] flex flex-col min-h-full overflow-hidden">
            <CardHeader className="p-0 shrink-0">
              <CardTitle className="flex items-center text-[14px] leading-[18px] font-bold text-[var(--primary-text-color)] mb-5">
                <CircleCheckBig className="h-5 w-5 text-[#676879] mr-2" />
                Ready ({readyOrders.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="animate-pulse bg-gray-200 h-32 rounded-[8px]" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {readyOrders.length === 0 ? (
                    <p className="text-gray-400 text-[13px] text-center py-8">No orders ready</p>
                  ) : (
                    readyOrders.map((order: any) =>
                      renderOrderCard(order, "border border-[#258750] text-[#258750] hover:bg-[#258750] hover:text-[#ffffff]")
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
